import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLangMap } from '../../hooks/useLanguages';
import { fetchData } from '../../services/dataApi';
import { LearningProgressService } from '../../services/LearningProgressService';

function useCategories(t) {
  return {
    emails: [t('writing.categories.emails'), '✉️'],
    applications_requests: [t('writing.categories.applicationsRequests'), '📝'],
    complaints_grievances: [t('writing.categories.complaintsGrievances'), '📣'],
  };
}

function localizedLesson(source, strings = {}) {
  const value = (id, fallback) => strings[id] ?? fallback;
  return {
    ...source,
    title: value('title', source.title), purpose: value('purpose', source.purpose),
    model: {
      fields: source.model.fields.map((field) => ({ ...field, label: value(`field_${field.id}_label`, field.label), value: value(`field_${field.id}_value`, field.value) })),
      blocks: source.model.blocks.map((block) => ({ ...block, text: value(`block_${block.id}_text`, block.text) })),
    },
    usefulPhrases: source.usefulPhrases.map((phrase, i) => ({ function: value(`phrase_0${i + 1}_function`, phrase.function), text: value(`phrase_0${i + 1}_text`, phrase.text) })),
    writingChecklist: source.writingChecklist.map((item, i) => value(`check_0${i + 1}`, item)),
    commonMistakes: source.commonMistakes.map((mistake, i) => ({ avoid: value(`mistake_0${i + 1}_avoid`, mistake.avoid), prefer: value(`mistake_0${i + 1}_prefer`, mistake.prefer), reason: value(`mistake_0${i + 1}_reason`, mistake.reason) })),
    practice: {
      guided: { ...source.practice.guided, instruction: value('practice_guided_instruction', source.practice.guided.instruction), requiredDetails: source.practice.guided.requiredDetails.map((item, i) => value(`practice_guided_detail_0${i + 1}`, item)) },
      independent: { ...source.practice.independent, instruction: value('practice_independent_instruction', source.practice.independent.instruction), requiredDetails: source.practice.independent.requiredDetails.map((item, i) => value(`practice_independent_detail_0${i + 1}`, item)) },
    },
  };
}

function withNativeGuidance(targetLesson, nativeLesson) {
  return {
    ...targetLesson,
    nativePurpose: nativeLesson.purpose,
    model: {
      ...targetLesson.model,
      fields: targetLesson.model.fields.map((field, index) => ({
        ...field,
        label: nativeLesson.model.fields[index]?.label ?? field.label,
      })),
    },
    usefulPhrases: targetLesson.usefulPhrases.map((phrase, index) => ({
      ...phrase,
      function: nativeLesson.usefulPhrases[index]?.function ?? phrase.function,
    })),
    writingChecklist: nativeLesson.writingChecklist,
    commonMistakes: targetLesson.commonMistakes.map((mistake, index) => ({
      ...mistake,
      reason: nativeLesson.commonMistakes[index]?.reason ?? mistake.reason,
    })),
    practice: {
      guided: {
        ...targetLesson.practice.guided,
        instruction: nativeLesson.practice.guided.instruction,
        requiredDetails: nativeLesson.practice.guided.requiredDetails,
      },
      independent: {
        ...targetLesson.practice.independent,
        instruction: nativeLesson.practice.independent.instruction,
        requiredDetails: nativeLesson.practice.independent.requiredDetails,
      },
    },
  };
}

export default function WritingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const { langMap: languages } = useLangMap();
  const target = languagePrefs?.targetLanguage || 'en';
  const lang = languages[target];
  const CATEGORY = useCategories(t);
  const [manifest, setManifest] = useState(null);
  const [activeCategory, setActiveCategory] = useState('emails');
  const [lesson, setLesson] = useState(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([
      fetchData('writing/en/index.json'),
      target === 'en' ? Promise.resolve(null) : fetchData(`writing/${target}/index.json`).catch(() => null),
    ]).then(([source, overlay]) => {
      if (!active) return;
      setManifest({ ...source, title: overlay?.title ?? source.title, description: overlay?.description ?? source.description });
    })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [target]);

  const lessons = useMemo(() => manifest?.lessons?.filter((item) => item.category === activeCategory) ?? [], [manifest, activeCategory]);

  async function openLesson(item) {
    setError(false); setDraft('');
    try {
      const source = await fetchData(`writing/en/${item.path}`);
      const native = languagePrefs?.nativeLanguage || 'en';
      const [targetOverlay, nativeOverlay] = await Promise.all([
        target === 'en' ? Promise.resolve(null) : fetchData(`writing/${target}/${item.path}`).catch(() => null),
        native === 'en' ? Promise.resolve(null) : fetchData(`writing/${native}/${item.path}`).catch(() => null),
      ]);
      const targetLesson = localizedLesson(source, targetOverlay?.strings);
      const nativeLesson = localizedLesson(source, nativeOverlay?.strings);
      setLesson(withNativeGuidance(targetLesson, nativeLesson));
      LearningProgressService.record(
        `${languagePrefs?.nativeLanguage || 'en'}_${target}`,
        'writing',
        1,
        { activityId: `lesson:${item.path}` },
      );
    } catch (_) { setError(true); }
  }

  if (lesson) return (
    <div className="min-h-screen bg-slate-50 px-4 py-8"><div className="mx-auto max-w-3xl">
      <button onClick={() => setLesson(null)} className="mb-5 font-bold text-indigo-600">{t('writing.allLessons')}</button>
      <div className="rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-700 p-7 text-white shadow-xl"><p className="text-xs font-black uppercase tracking-widest">{lesson.level} · {lesson.channel.replace('_', ' ')}</p><h1 className="mt-2 text-3xl font-black">{lesson.title}</h1><p className="mt-2 text-violet-100">{lesson.nativePurpose}</p></div>
      <section className="mt-6 rounded-3xl border bg-white p-6 shadow-sm"><h2 className="text-lg font-black">{t('writing.model')}</h2>{lesson.model.fields.map((f) => <div key={f.id} className="mt-4 rounded-xl bg-slate-100 p-3"><b>{f.label}:</b> {f.value}</div>)}<div dir={lang?.dir || 'ltr'} className="mt-4 space-y-3">{lesson.model.blocks.map((b) => <p key={b.id} className="leading-relaxed text-slate-700">{b.text}</p>)}</div></section>
      <section className="mt-6 grid gap-6 md:grid-cols-2"><div className="rounded-3xl border bg-white p-6"><h2 className="font-black">{t('writing.usefulPhrases')}</h2>{lesson.usefulPhrases.map((p, i) => <div key={i} className="mt-3"><p className="text-xs font-bold uppercase text-violet-600">{p.function}</p><p>{p.text}</p></div>)}</div><div className="rounded-3xl border bg-white p-6"><h2 className="font-black">{t('writing.beforeYouSend')}</h2>{lesson.writingChecklist.map((x, i) => <label key={i} className="mt-3 flex gap-2 text-sm"><input type="checkbox" />{x}</label>)}</div></section>
      {lesson.commonMistakes.length > 0 && <section className="mt-6 rounded-3xl border border-amber-200 bg-amber-50 p-6"><h2 className="text-lg font-black text-amber-950">{t('writing.commonMistakes', { defaultValue: 'Common mistakes' })}</h2>{lesson.commonMistakes.map((mistake, i) => <div key={i} className="mt-4 rounded-2xl bg-white p-4"><p className="text-sm text-rose-700 line-through">{mistake.avoid}</p><p className="mt-1 font-bold text-emerald-700">{mistake.prefer}</p><p className="mt-2 text-sm text-slate-600">{mistake.reason}</p></div>)}</section>}
      <section className="mt-6 rounded-3xl border bg-white p-6"><h2 className="text-lg font-black">{t('writing.yourTurn')}</h2><p className="mt-2 text-slate-600">{lesson.practice.guided.instruction}</p><ul className="mt-3 list-inside list-disc text-sm text-slate-500">{lesson.practice.guided.requiredDetails.map((x) => <li key={x}>{x}</li>)}</ul><textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={9} placeholder={t('writing.writePlaceholder')} className="mt-4 w-full rounded-2xl border border-slate-200 p-4 outline-none focus:border-violet-500"/><p className="mt-2 text-right text-xs font-bold text-slate-400">{t('writing.wordsCount', { count: draft.trim() ? draft.trim().split(/\s+/).length : 0 })}</p></section>
    </div></div>
  );

  return <div className="min-h-screen bg-slate-50 px-4 py-8"><div className="mx-auto max-w-5xl"><button onClick={() => navigate('/')} className="mb-5 font-bold text-indigo-600">{t('writing.learningPath')}</button><div className="rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-700 p-8 text-white"><p className="text-4xl">✍️</p><h1 className="mt-3 text-3xl font-black">{manifest?.title || t('writing.title')}</h1><p className="mt-2 max-w-2xl text-violet-100">{manifest?.description || t('writing.description')}</p></div>
    <div className="mt-6 flex gap-2 overflow-x-auto">{Object.entries(CATEGORY).map(([id, [label, icon]]) => <button key={id} onClick={() => setActiveCategory(id)} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold ${activeCategory === id ? 'bg-violet-600 text-white' : 'border bg-white text-slate-600'}`}>{icon} {label}</button>)}</div>
    {error && <p className="mt-6 rounded-xl bg-rose-50 p-4 text-rose-700">{t('writing.loadError')}</p>}
    {loading && !error && <div className="mt-6 grid gap-4 md:grid-cols-2">{[1, 2, 3, 4].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>}
    {!loading && !error && <div className="mt-6 grid gap-4 md:grid-cols-2">{lessons.map((item) => <button key={item.id} onClick={() => openLesson(item)} className="rounded-2xl border bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md"><div className="flex items-center justify-between"><span className="rounded-full bg-violet-100 px-2 py-1 text-xs font-black uppercase text-violet-700">{item.level}</span><span className="text-slate-300">→</span></div><h2 className="mt-3 font-black text-slate-900">{item.title}</h2><p className="mt-1 text-sm text-slate-500">{item.purpose}</p></button>)}</div>}</div></div>;
}
